import LocalScene from '../projections';
import {
  CloudNode,
  RawPointCloud
} from '.';
import {
  LineSegments,
  Points,
  Vector3
} from 'three';

export class RawPointCloudNode extends CloudNode {
  protected cloud: RawPointCloud;
  public parent: RawPointCloudRootNode;
  protected children = [];

  constructor(cloud: RawPointCloud, parent = null) {
    super();

    this.cloud = cloud;
    this.parent = parent;
  }

  loadFromData(data) {
    this.data = data;

    this.numPoints = data.numPoints;
    this.tightBoundingBox = {
      min: data.xyz_min,
      max: data.xyz_max
    };

    // Calculate bounding box size
    let size = new Vector3()
      .add(data.xyz_max)
      .sub(data.xyz_min);

    // Calculate bounding box center
    let center = new Vector3()
      .add(data.xyz_min)
      .add(data.xyz_max)
      .divideScalar(2.0)
      .sub(data.offset);

    this.sizeTight.copy(size);
    this.size = Math.max(size.x, size.y, size.z);
    this._center = center;

    // Safe to add to parent's child list now
    this.parent.addChildNode(this);

    // Update root node info
    LocalScene.offsetToScene(this.data.offset);
    this.cloud.updateBoundingBox(this.tightBoundingBox);
    this.cloud.updateRootNode();
  }

  get shift() {
    return LocalScene.offsetToScene(this.data.offset);
  }

  getGeometry(data) {
    this.setGeometry(data);

    // Update unique values and min/max values
    this.cloud.updateClassifications(data.classification);
    this.cloud.updateColorLimits(data.rgb_max);
    this.cloud.updateIntensityRange(data.intensity_min, data.intensity_max);
  }

  addToScene() {
    // Create the pointcloud
    let pointsScene = new Points(this.geometry,
      this.cloud.materialScene);
    let pointsPicker = new Points(this.geometry,
      this.cloud.materialPicker);

    // Create bounding box object;
    let boundingBox = this.cloud.generateEdgeMesh(
      this.sizeTight.x, this.sizeTight.y, this.sizeTight.z);

    this.addCloudData(pointsPicker, pointsScene, boundingBox);
  }

  load() {
    if ((this.loaded) || (this.loading)) {
      return;
    }

    this.getGeometry(this.data);
    this.loaded = true;
    this.loading = false;
  }
}

/* Placeholder root node to handle our required format */
export class RawPointCloudRootNode extends RawPointCloudNode {
  constructor(cloud) {
    super(cloud);

    this.numPoints = 0;
    this.boundingBox = new LineSegments();
    this._center = new Vector3();
    this.size = 0;
  }

  get center() {
    return this._center;
  }

  /** Function override */
  dispose() {
    this.pointsScene = null;
    this.pointsPicker = null;
    this.boundingBox = null;
  }

  updateSizeInfo(data) {
    // Calculate bounding box size
    let size = new Vector3()
      .add(data.max)
      .sub(data.min);

    this.size = Math.max(size.x, size.y, size.z);

    // Calculate bounding box center
    let center = new Vector3()
      .add(data.min)
      .add(data.max)
      .divideScalar(2.0);

    this._center = center;

    // Reset our sphere to null (so we re-calculate next time)
    this._sphere = null;
  }

  getGeometry() {
    return;
  }

  addChildNode(node) {
    this.children.push(node);
  }

  addToScene() {
    this.pointsScene = new Points();
    this.pointsPicker = new Points();
  }
}
